import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AdminRole, ChannelPaymentStatus, ChannelPaymentSubStatus, trimDecimalZeros } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/status-badge";
import { Amount } from "@/components/amount";
import { AssetLabel } from "@/components/asset-label";
import { HashLink } from "@/components/hash-link";
import { CHANNEL_PAYMENT_STATUS_COLOR, TRANSACTION_STATUS_COLOR } from "@/lib/status-colors";
import { formatStatusLabel } from "@/lib/utils";
import { hasRole } from "@/lib/roles";
import { ApiError } from "@/api/client";
import { acceptChannelAmlCase, refundChannelAmlCase, rejectChannelAmlCase } from "@/api/aml";
import { getChannelPayment } from "@/api/channels";
import { type AmlActionErrorCode, type ChannelPaymentDetailResponse } from "@/api/types";

// Task 14 (feature-031 Phase B): the channel payment 360° detail page, with
// the AML audit block when resolved (mirrors
// _app.invoices_.$invoiceId.tsx's "Resolution"/"AML resolution" sections)
// and, when the payment is ACTION_REQUIRED/HIGH_RISK, the CHANNELS_MANAGER-
// only accept/reject/refund actions — the same three-dialog pattern
// _app.high-risk.tsx's invoice queue uses, but CHANNELS_MANAGER-gated (per
// aml.controller.ts's method-level `@Roles` override on these three routes,
// not AML_OFFICER) and decision-only: the refund dialog has NO address
// field, matching the plan's explicit instruction and the existing invoice
// AML refund's own shape (the merchant supplies the payout address later,
// from their own portal).
export const Route = createFileRoute("/_app/channel-payments_/$channelPaymentId")({
  component: ChannelPaymentDetailPage,
});

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

/** Mirrors _app.invoices_.$invoiceId.tsx's own formatCrypto helper. */
function formatCrypto(amount: string | null, symbol: string): string {
  return amount === null ? "—" : `${trimDecimalZeros(amount)} ${symbol}`;
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium" style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </dd>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

const GUIDANCE_BOX = "rounded-md border border-[#F59E0B]/30 bg-[#F59E0B]/5 px-3 py-2 text-sm text-[#92400E]";
const ERROR_BOX = "rounded-md border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 text-sm text-[#EF4444]";

// ─── action dialogs (decision-only — no address field on any of the three) ─

interface ActionDialogProps {
  channelPaymentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (data: ChannelPaymentDetailResponse) => void;
}

function errorCode(err: unknown): AmlActionErrorCode | null {
  return err instanceof ApiError ? (err.code as AmlActionErrorCode) : null;
}

function RejectDialog({ channelPaymentId, open, onOpenChange, onSuccess }: ActionDialogProps) {
  const [note, setNote] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNote("");
      setErrorMessage(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => rejectChannelAmlCase(channelPaymentId, note.trim() || undefined),
    onSuccess: (data) => {
      toast.success("Channel payment rejected.");
      onSuccess(data);
    },
    onError: (err) => {
      setErrorMessage(err instanceof ApiError ? err.message : "Failed to reject this payment.");
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject this payment?</DialogTitle>
          <DialogDescription>
            The channel payment becomes CANCELLED. This cannot be undone — there is no way to reverse a
            rejection from the backoffice.
          </DialogDescription>
        </DialogHeader>

        {errorMessage && <div className={ERROR_BOX}>{errorMessage}</div>}

        <div className="space-y-1.5">
          <Label htmlFor="reject-note">Note (optional)</Label>
          <Textarea id="reject-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for rejecting this payment…" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reject payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RefundDialog({ channelPaymentId, open, onOpenChange, onSuccess }: ActionDialogProps) {
  const [note, setNote] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isGuidance, setIsGuidance] = useState(false);

  useEffect(() => {
    if (open) {
      setNote("");
      setErrorMessage(null);
      setIsGuidance(false);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => refundChannelAmlCase(channelPaymentId, note.trim() || undefined),
    onSuccess: (data) => {
      toast.success("Refund opened.");
      onSuccess(data);
    },
    onError: (err) => {
      setErrorMessage(err instanceof ApiError ? err.message : "Failed to open a refund.");
      setIsGuidance(errorCode(err) === "STILL_FROZEN");
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Open a refund for this payment?</DialogTitle>
          <DialogDescription>
            A new OPEN refund is created for this channel payment, with no payout address yet — the
            merchant claims it from their own portal by supplying one. Status stays ACTION_REQUIRED /
            HIGH_RISK until then.
          </DialogDescription>
        </DialogHeader>

        <div className={GUIDANCE_BOX}>
          Unfreeze the deposit in the Fireblocks Console first, then retry — a refund cannot open while
          any deposit on this payment is still frozen.
        </div>

        {errorMessage && <div className={isGuidance ? GUIDANCE_BOX : ERROR_BOX}>{errorMessage}</div>}

        <div className="space-y-1.5">
          <Label htmlFor="refund-note">Note (optional)</Label>
          <Textarea id="refund-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for refunding this payment…" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Open refund"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AcceptDialog({ channelPaymentId, open, onOpenChange, onSuccess }: ActionDialogProps) {
  const [note, setNote] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isGuidance, setIsGuidance] = useState(false);

  useEffect(() => {
    if (open) {
      setNote("");
      setErrorMessage(null);
      setIsGuidance(false);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => acceptChannelAmlCase(channelPaymentId, note.trim() || undefined),
    onSuccess: (data) => {
      toast.success("Channel payment accepted.");
      onSuccess(data);
    },
    onError: (err) => {
      setErrorMessage(err instanceof ApiError ? err.message : "Failed to accept this payment.");
      setIsGuidance(errorCode(err) === "STILL_FROZEN");
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Accept this payment?</DialogTitle>
          <DialogDescription>
            The payment returns to CONFIRMED and resumes its normal path toward settlement, exactly as
            any other confirmed payment on this channel.
          </DialogDescription>
        </DialogHeader>

        <div className={GUIDANCE_BOX}>
          Unfreeze the deposit in the Fireblocks Console first, and confirm it shows Cleared, before
          confirming Accept here.
        </div>

        {errorMessage && <div className={isGuidance ? GUIDANCE_BOX : ERROR_BOX}>{errorMessage}</div>}

        <div className="space-y-1.5">
          <Label htmlFor="accept-note">Note (optional)</Label>
          <Textarea id="accept-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note…" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accept payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChannelPaymentDetailPage() {
  const { channelPaymentId } = Route.useParams();
  const { admin } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const canResolveAml = hasRole(admin.roles, AdminRole.CHANNELS_MANAGER);
  const [activeAction, setActiveAction] = useState<"reject" | "refund" | "accept" | null>(null);

  const {
    data: payment,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["channel-payment", channelPaymentId],
    queryFn: () => getChannelPayment(channelPaymentId),
  });

  const invalidate = (data: ChannelPaymentDetailResponse) => {
    queryClient.setQueryData(["channel-payment", channelPaymentId], data);
    queryClient.invalidateQueries({ queryKey: ["channel-payments"] });
    setActiveAction(null);
  };

  if (isPending) {
    return <div className="py-10 text-center text-sm text-muted-foreground">Loading channel payment…</div>;
  }
  if (isError || !payment) {
    return <div className="py-10 text-center text-sm text-muted-foreground">Failed to load channel payment.</div>;
  }

  const isAmlEligible =
    canResolveAml &&
    payment.status === ChannelPaymentStatus.ACTION_REQUIRED &&
    payment.subStatus === ChannelPaymentSubStatus.HIGH_RISK;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{payment.publicId}</h2>
          <p className="text-sm text-muted-foreground">
            {payment.merchant.businessName} · {payment.merchant.email}
          </p>
        </div>
        <StatusBadge
          label={formatStatusLabel(payment.subStatus ?? payment.status)}
          color={CHANNEL_PAYMENT_STATUS_COLOR[payment.status]}
        />
      </div>

      <SectionCard title="Summary">
        <dl className="space-y-2 text-sm">
          <DetailRow
            label="Channel"
            value={
              <Link to="/channels/$channelId" params={{ channelId: payment.channel.id }} className="hover:underline">
                {payment.channel.label} <span className="font-mono text-xs text-muted-foreground">({payment.channel.publicId})</span>
              </Link>
            }
          />
        </dl>
        <div className="my-4 flex flex-wrap items-end justify-between gap-4 border-b pb-4">
          <Amount native={payment.cryptoAmount} symbol={payment.asset.symbol} usd={payment.cryptoAmountUsd} inline />
          <AssetLabel symbol={payment.asset.symbol} network={payment.asset.network} inline />
        </div>
        <dl className="space-y-2 text-sm">
          <DetailRow label="Fee" value={formatCrypto(payment.cryptoFee, payment.asset.symbol)} />
          <DetailRow label="USD rate" value={payment.usdRate} />
          <DetailRow label="Created" value={formatTimestamp(payment.createdAt)} />
        </dl>
      </SectionCard>

      <SectionCard title="Transactions">
        {payment.transactions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No transactions yet.</p>
        ) : (
          <div className="overflow-hidden overflow-x-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted text-left font-semibold text-muted-foreground">
                  <th className="px-3 py-2">Operation</th>
                  <th className="px-3 py-2">Tx hash</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Sender</th>
                  <th className="px-3 py-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {payment.transactions.map((tx) => (
                  <tr key={tx.id} className="border-t">
                    <td className="px-3 py-2 font-medium">{tx.operation}</td>
                    <td className="px-3 py-2">
                      <HashLink value={tx.txHash} type="tx" network={tx.asset.network} />
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge label={formatStatusLabel(tx.status)} color={TRANSACTION_STATUS_COLOR[tx.status]} />
                    </td>
                    <td className="px-3 py-2 text-right" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatCrypto(tx.amount, tx.asset.symbol)}
                    </td>
                    <td className="px-3 py-2">
                      <HashLink value={tx.counterpartyAddress} type="address" network={tx.asset.network} />
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{formatTimestamp(tx.chainStartedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {payment.aml && (
        <SectionCard title="AML resolution">
          <dl className="space-y-2 text-sm">
            <DetailRow label="Resolved by" value={payment.aml.amlResolvedBy.displayName} />
            <DetailRow label="Resolved at" value={formatTimestamp(payment.aml.amlResolvedAt)} />
            <DetailRow label="Action" value={payment.aml.amlResolutionAction} />
            <DetailRow label="Note" value={payment.aml.amlResolutionNote ?? "—"} />
          </dl>
        </SectionCard>
      )}

      {isAmlEligible && (
        <SectionCard title="AML actions">
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="destructive" onClick={() => setActiveAction("reject")}>
              Reject
            </Button>
            <Button variant="outline" onClick={() => setActiveAction("refund")}>
              Refund
            </Button>
            <Button onClick={() => setActiveAction("accept")}>Accept</Button>
          </div>
        </SectionCard>
      )}

      <RejectDialog
        channelPaymentId={payment.id}
        open={activeAction === "reject"}
        onOpenChange={(next) => setActiveAction(next ? "reject" : null)}
        onSuccess={invalidate}
      />
      <RefundDialog
        channelPaymentId={payment.id}
        open={activeAction === "refund"}
        onOpenChange={(next) => setActiveAction(next ? "refund" : null)}
        onSuccess={invalidate}
      />
      <AcceptDialog
        channelPaymentId={payment.id}
        open={activeAction === "accept"}
        onOpenChange={(next) => setActiveAction(next ? "accept" : null)}
        onSuccess={invalidate}
      />
    </div>
  );
}
